import { useSession } from "@internal/auth";
import { useEffect, useState } from "react";
import { getDbForUser, type PlannerDoc } from "@/lib/db";
import { parseFoodClarifications, parseRecipes } from "@/lib/repos/settings";
import {
  DEFAULT_SETTINGS_DATA,
  type SettingsData,
  settingsId,
} from "@/lib/schema";

interface UseSettingsResult {
  settings: SettingsData;
  loading: boolean;
}

export function useSettings(): UseSettingsResult {
  const { user } = useSession();
  const [state, setState] = useState<UseSettingsResult>({
    settings: { widgets: [] },
    loading: true,
  });

  useEffect(() => {
    if (!user) {
      setState({ settings: { widgets: [] }, loading: false });
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;
    (async () => {
      const db = await getDbForUser(user.id);
      if (cancelled) return;
      sub = db.daily_planner.findOne(settingsId(user.id)).$.subscribe((doc) => {
        const data = doc
          ? ((doc.toJSON() as PlannerDoc).data as unknown as SettingsData)
          : DEFAULT_SETTINGS_DATA;
        const foodClarifications = parseFoodClarifications(
          data.foodClarifications,
        );
        const recipes = parseRecipes(data.recipes);
        setState({
          settings: {
            widgets: Array.isArray(data.widgets) ? data.widgets : [],
            ...(foodClarifications.length > 0 ? { foodClarifications } : {}),
            ...(recipes.length > 0 ? { recipes } : {}),
          },
          loading: false,
        });
      });
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [user]);

  return state;
}
